import { useState, useImperativeHandle, forwardRef, useEffect } from "react";
import { Button, Card, Form } from "react-bootstrap";
import FormikSelectField from "../../../../shared/components/FormikSelectField";
import { ESCALATION_FIELDS_CONFIG } from "../config/escalationConfig";
import { getActionableFields } from "../utils/escalationFields";
import { useDispatch, useSelector } from "react-redux";
import { loadDropdownCacheRequest } from "../redux/Actions";

const ActionsSection = forwardRef(({ isEdit, initialActions }, ref) => {
    const allFields = getActionableFields(ESCALATION_FIELDS_CONFIG);

    const dispatch = useDispatch();
    const dropdownCache = useSelector((state) => state.dropdown.cache); //read cached data

    const [actions, setActions] = useState([{ id: 1, field: "", value: "" }]);

    const [errors, setErrors] = useState({});

    const SYSTEM_FIELDS = new Set(["is_auto_close"]);


     /** escalation Pre-fill actions when editing */
useEffect(() => {
  if (!isEdit || !initialActions.length) return;

  const formatted = initialActions
  .filter((item) => !SYSTEM_FIELDS.has(item.field_code))
  .map((item, idx) => {
    const fieldMeta = allFields.find((f) => f.value === item.field_code);
    return {
      id: idx + 1,
      field: item.field_code,
      value: item.value,
      fieldType: fieldMeta?.type,
      selectedLabel: item.valueLabel || "",
      staticOptions: fieldMeta?.staticOptions || [],
      dataSource: fieldMeta?.dataSource,
      apiEndpoint: fieldMeta?.apiEndpoint,
      apiOptions: [], // ← always empty first
    };
  });

  setActions(formatted);

}, [isEdit, initialActions]); // ← no dropdownCache

useEffect(() => {
  actions.forEach((item) => {
    if (item.dataSource === "api" && item.apiEndpoint && !dropdownCache[item.apiEndpoint]) {
      dispatch(loadDropdownCacheRequest(item.apiEndpoint));
    }
  });
}, [actions]); // ← watch actions (NOT dropdownCache)


    useEffect(() => {
    setActions((prev) =>
      prev.map((a) => {
        if (!a.apiEndpoint) return a;

        const cached = dropdownCache[a.apiEndpoint];
        if (!cached) return a;

        // only update if changed
        if (a.apiOptions === cached) return a;

        return { ...a, apiOptions: cached };
      })
    );
  }, [dropdownCache]);
    
     const handleAddAction = () => {
    setActions((prev) => [
      ...prev,
      { id: prev.length + 1, field: "", value: "" },
    ]);
  };

  /** escalation Field change */
  const handleFieldChange = (index, fieldCode) => {
    const fieldMeta = allFields.find((f) => f.value === fieldCode);
    const endpoint = fieldMeta?.apiEndpoint;

    setActions((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        field: fieldCode,
        value: "",
        fieldType: fieldMeta?.type,
        dataSource: fieldMeta?.dataSource,
        staticOptions: fieldMeta?.staticOptions || [],
        apiEndpoint: endpoint,
        apiOptions: dropdownCache[endpoint] || [],
      };
      return updated;
    });

      // Clear error for this row when field is selected
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });

    // fetch API-based dropdown if not cached
    if (fieldMeta?.dataSource === "api" && endpoint && !dropdownCache[endpoint]) {
      dispatch(loadDropdownCacheRequest(endpoint));
    }
  };

 /** escalation Value change */
  const handleValueChange = (index, val,selectedLabel) => {

    setActions((prev) => {
      const updated = [...prev];
      updated[index].value = val;
      updated[index].selectedLabel = selectedLabel ;
      return updated;
    });

    // Clear error as soon as user selects a value
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

    /** escalation Delete an action row */
  const handleDelete = (id) => {
    setActions((prev) =>
      prev
        .filter((a) => a.id !== id)
        .map((a, i) => ({ ...a, id: i + 1 }))
    );
  };

   /** escalation Expose methods to parent */
  useImperativeHandle(ref, () => ({
    validateAndGetData: () => {
      const errs = {};
      let isValid = true;

      // Check each action row for partial/incomplete entries
      actions.forEach((a, i) => {
        const hasField = a.field && a.field.trim() !== "";
        const hasValue =
          a.value !== null &&
          a.value !== undefined &&
          !(typeof a.value === "string" && a.value.trim() === "");

        // If user started filling this row, both field and value are required
        if (hasField || hasValue) {
          if (!hasField) {
            errs[i] = "Please select a field.";
            isValid = false;
          } else if (!hasValue) {
            errs[i] = "Please select or enter a value.";
            isValid = false;
          }
        }
      });

      setErrors(errs);

      // Only include complete action rows in the final data
      const cleanedData = actions
        .filter((a) => {
          const hasField = a.field && a.field.trim() !== "";
          const hasValue =
            a.value !== null &&
            a.value !== undefined &&
            !(typeof a.value === "string" && a.value.trim() === "");
          return hasField && hasValue;
        })
        .map((a) => ({
          field_code: a.field,
          field_name:
            allFields.find((f) => f.value === a.field)?.label || a.field,
          value: a.value,
          valueLabel: a.selectedLabel || String(a.value),
        }));

      const hasClosedStatus = cleanedData.some(
        (a) => a.field_code === "status" && a.value === 6
      );

      const hasAutoClose = cleanedData.some(
        (a) => a.field_code === "is_auto_close"
      );

      if (hasClosedStatus && !hasAutoClose) {
        cleanedData.push({
          field_code: "is_auto_close",
          field_name: "Auto Close",
          value: true,
          valueLabel: "true",
        });
      }

      return { isValid, data: cleanedData };
    },
  }));



    return (
      <Card>
        {/* <div className="actions-section border rounded p-3 bg-white shadow-sm"> */}
        <Card.Header>Actions</Card.Header>

        <div className="d-flex justify-content-end m-2">
          {/* <h5 className="fw-semibold mb-0">Actions</h5> */}
          <Button
            className="default-btn-color"
            size="sm"
            onClick={handleAddAction}
          >
            + Add Action
          </Button>
        </div>

        {actions.map((action, index) => {
          const fieldDef = allFields.find((f) => f.value === action.field);

          const showStaticDropdown =
            fieldDef?.type === "select" && fieldDef?.staticOptions?.length > 0;

          const showApiDropdown = fieldDef?.dataSource === "api";

          return (
            <div key={action.id} className="mb-2">
              <div
                className="d-flex align-items-center border rounded px-2 py-2"
                style={{ backgroundColor: "#f9fafb" }}
              >
                {/* Action number */}
                <div
                  className="rounded-circle text-white fw-bold d-flex align-items-center justify-content-center me-2"
                  style={{
                    width: 26,
                    height: 26,
                    backgroundColor: "#16a34a",
                    fontSize: "0.8rem",
                  }}
                >
                  {action.id}
                </div>

                {/* Field dropdown */}
                <div className="me-2" style={{ width: "50%" }}>
                  <FormikSelectField
                    name={`field-${index}`}
                    value={action.field}
                    onChange={(_, val) => handleFieldChange(index, val)}
                    placeholder="Select field"
                  >
                    {allFields.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </FormikSelectField>
                </div>

                <span className="mx-2 fw-bold">=</span>

                {/* Value input/dropdown */}
                <div className="me-2" style={{ width: "50%" }}>
                  {showStaticDropdown ? (
                    <FormikSelectField
                      name={`value-${index}`}
                      value={action.value}
                      onChange={(_, val, selectedLabel) =>
                        handleValueChange(index, val, selectedLabel)
                      }
                      placeholder="Select value"
                    >
                      {fieldDef.staticOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </FormikSelectField>
                  ) : showApiDropdown ? (
                    <FormikSelectField
                      name={`value-${index}`}
                      value={action.value}
                      onChange={(_, val, selectedLabel) =>
                        handleValueChange(index, val, selectedLabel)
                      }
                      placeholder="Select value"
                    >
                      {action.apiOptions?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </FormikSelectField>
                  ) : (
                    <Form.Control
                      type="text"
                      placeholder="Enter value"
                      value={action.value}
                      onChange={(e) => handleValueChange(index, e.target.value)}
                      style={{ padding: "10px" }}
                    />
                  )}
                </div>

                {/* Delete icon */}
                {actions.length > 1 && (
                  <div
                    className="text-danger action-icon"
                    style={{ fontSize: "20px", cursor: "pointer" }}
                    onClick={() => handleDelete(action.id)}
                  >
                    🗑️
                  </div>
                )}
              </div>

              {/* Error message */}
              {errors[index] && (
                <div className="text-danger small mt-1 ms-5">
                  {errors[index]}
                </div>
              )}
            </div>
          );
        })}
        {/* </div> */}
      </Card>
    );
});

export default ActionsSection;
